import {
	ChangeDetectionStrategy,
	Component,
	computed,
	DestroyRef,
	EventEmitter,
	inject,
	input,
	Input,
	OnInit,
	Output,
	signal,
	WritableSignal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import {
	ILocationTreeNode,
	LocationTreeNodeType,
	LocationTreeSelectorService,
} from '@shared/ui-components/au-locations-tree-selector/location-tree-selector.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
	selector: 'location-tree-node',
	imports: [CommonModule, MatButtonModule, MatIconModule, MatCheckboxModule],
	templateUrl: './location-tree-node.component.html',
	styleUrls: ['./location-tree-node.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LocationTreeNodeComponent implements OnInit {
	private readonly locationTreeService = inject(LocationTreeSelectorService);
	private readonly destroyRef = inject(DestroyRef);

	disableNestedSelection = input<boolean>(false);
	@Input({ required: true }) locationNode: LocationTreeNodeType;
	@Input() level: number = 0;
	@Input() disabledCheckboxes = false;
	@Input() active: boolean = false;
	protected allowDeselectForSingleSelect = input(true);

	@Output() locationSelected = new EventEmitter<ILocationTreeNode>();
	treeNode = signal<ILocationTreeNode | null>(null);
	isSelected = signal(false);

	descendantsAllSelected = computed(() => {
		const childrenIds = this.treeNode()?.children;
		return childrenIds.every(child => this.locationTreeService.getNode(child)?.selected);
	});

	descendantsPartiallySelected = computed(() => {
		const childrenIds = this.treeNode()?.children;
		const someSelected = childrenIds.some(
			child => this.locationTreeService.getNode(child)?.selected
		);

		return someSelected && !this.descendantsAllSelected();
	});

	parentNode = signal<ILocationTreeNode | null>(null);
	isExpanded: WritableSignal<boolean> = signal(false);

	ngOnInit() {
		this.treeNode.set(this.locationTreeService.getNode(this.locationNode.id));
		this.isSelected.set(this.treeNode()?.selected || false);

		this.locationTreeService.flatTreeNodesChanged$
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => {
				this.treeNode.set(this.locationTreeService.getNode(this.locationNode.id));
				this.isSelected.set(this.treeNode()?.selected || false);
			});

		this.subscribeToKeyboardEvents();
	}

	hasChildrens(): boolean {
		return this.locationTreeService.getNode(this.treeNode()?.id)?.children.length > 0;
	}

	toggleExpand(): void {
		this.treeNode().isExpanded = !this.treeNode().isExpanded;
		this.isExpanded.set(this.treeNode().isExpanded);
		this.updateFlatTreeNode();
		this.locationTreeService.expanded();
	}

	toggleNodeSelection() {
		if (
			!this.allowDeselectForSingleSelect() &&
			this.disabledCheckboxes &&
			this.treeNode().selected
		)
			return;
		this.treeNode().selected = !this.treeNode().selected;
		this.updateFlatTreeNode();
		this.locationSelected.emit(this.treeNode());
	}

	private updateFlatTreeNode() {
		this.locationTreeService.updateFlatTreeNodeMap(this.treeNode().id, {
			...this.treeNode(),
		});
	}

	subscribeToKeyboardEvents() {
		this.locationTreeService.triggerToggleSubject$
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(location => {
				if (location.id === this.treeNode().id) {
					this.toggleNodeSelection();
				}
			});

		this.locationTreeService.toggleExpand$
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(location => {
				if (location.id === this.treeNode().id) {
					this.toggleExpand();
				}
			});
	}
}
